/**
 * AgentChat — DeepSeek 스타일 실시간 스트리밍 AI 에이전트 채팅
 *
 * 5개 사이트 공통 React 컴포넌트.
 * 사고 과정 → 도구 호출 → 실시간 답변 스트리밍을 시각적으로 표시.
 *
 * Props:
 *   siteName   — 사이트명 (UI 표시용)
 *   endpoint   — SSE 엔드포인트 (기본: /api/store/assistant/stream)
 *   getHeaders — 인증 헤더 함수
 *   className  — 추가 CSS 클래스
 *   placeholder — 입력 placeholder
 *   floating   — true면 우측 하단 플로팅 위젯
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useAgentSSE from "../../hooks/useAgentSSE";

/* Treat model output as text/Markdown, never executable HTML. */
function SimpleMarkdown({ text }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
    a: ({ node, children, href, ...props }) => <a {...props} href={href} target={href?.startsWith("/") && !href.startsWith("//") ? undefined : "_blank"} rel="noopener noreferrer" className="agent-link">{children}</a>,
    h1: ({ node, children, ...props }) => <h3 {...props}>{children}</h3>,
    h2: ({ node, children, ...props }) => <h3 {...props}>{children}</h3>,
    table: ({ node, ...props }) => <table {...props} className="agent-md-table" />,
  }}>{text || ""}</ReactMarkdown>;
}

/* ─── Gemini-style 사고 과정 (접기/펼치기) ─── */
function ThinkingIndicator({ thoughts, isStreaming = true }) {
  const [open, setOpen] = useState(isStreaming);
  const label = thoughts.length > 0
    ? thoughts[0].replace(/^분석 중\.\.\. .*/, "생각하는 중").slice(0, 40)
    : "생각하는 중";

  if (!thoughts.length) return null;

  return (
    <div className="agent-thinking">
      <button
        onClick={() => setOpen(!open)}
        className="agent-thinking-header"
        style={{ cursor: "pointer", background: "none", border: "none", padding: 0, display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#6b7280" }}
      >
        {isStreaming && (
          <span className="agent-thinking-pulse" style={{ width: 8, height: 8, borderRadius: "50%", background: "#3b82f6", display: "inline-block", animation: "pulse 1.5s infinite" }} />
        )}
        <span className="agent-thinking-icon" style={{ color: "#3b82f6" }}>✦</span>
        <span style={{ fontWeight: 500 }}>{isStreaming ? "생각하는 중..." : label}</span>
        <span style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s", fontSize: "10px" }}>▼</span>
      </button>
      {open && (
        <div className="agent-thinking-list" style={{ marginLeft: 20, paddingLeft: 12, borderLeft: "2px solid #e5e7eb", marginTop: 6 }}>
          {thoughts.map((t, i) => (
            <div key={i} className="agent-thinking-item" style={{ fontSize: "11px", color: "#6b7280", fontStyle: "italic", lineHeight: 1.5 }}>{t}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── 도구 호출 표시 ─── */
function ToolCallIndicator({ tools }) {
  if (!tools.length) return null;
  return (
    <div className="agent-tools">
      {tools.map((tool, i) => (
        <div key={i} className={`agent-tool-item agent-tool-${tool.status}`}>
          <span className="agent-tool-icon">
            {tool.status === "running" ? "⏳" : "✅"}
          </span>
          <span className="agent-tool-name">{tool.name}</span>
          {tool.status === "done" && (
            <span className="agent-tool-badge">완료</span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── 스트리밍 텍스트 (커서 애니메이션) ─── */
function StreamingText({ text }) {
  if (!text) return null;
  return (
    <div className="agent-streaming">
      <SimpleMarkdown text={text} />
      <span className="agent-cursor" />
    </div>
  );
}

/* ─── 메인 컴포넌트 ─── */
export default function AgentChat({
  siteName = "AI 에이전트",
  endpoint = "/api/store/assistant/stream",
  getHeaders = () => ({}),
  className = "",
  placeholder = "무엇이든 물어보세요...",
  floating = false,
}) {
  const {
    messages,
    sendMessage,
    stop,
    reset,
    status,
    error,
    streamingText,
    currentThoughts,
    currentTools,
  } = useAgentSSE({ endpoint, getHeaders });

  const [input, setInput] = useState("");
  const [isOpen, setIsOpen] = useState(!floating);
  const messagesRef = useRef(null);
  const inputRef = useRef(null);

  // 외부 트리거: Navbar 등에서 toggle-ai-chat 이벤트로 열기/닫기
  useEffect(() => {
    if (!floating) return;
    const handler = () => setIsOpen(prev => !prev);
    window.addEventListener("toggle-ai-chat", handler);
    return () => window.removeEventListener("toggle-ai-chat", handler);
  }, [floating]);

  // 채팅 영역만 이동한다. scrollIntoView는 페이지 전체를 푸터로 밀어낸다.
  useEffect(() => {
    const container = messagesRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages, streamingText, currentThoughts, currentTools]);

  // 입력 포커스
  useEffect(() => {
    if (isOpen && status !== "streaming") {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [isOpen, status]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const msg = input.trim();
      if (!msg) return;
      setInput("");
      sendMessage(msg);
    },
    [input, sendMessage]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  /* ── 플로팅 모드: FAB 숨김 (Navbar AI 버튼이 대체) ── */
  if (floating && !isOpen) {
    return null;
  }

  return (
    <div className={`agent-chat ${floating ? "agent-chat-floating" : ""} ${className}`}>
      {/* 헤더 */}
      <div className="agent-chat-header">
        <div className="agent-chat-title">
          <span className="agent-chat-dot" />
          {siteName}
        </div>
        <div className="agent-chat-actions">
          {messages.length > 0 && (
            <button onClick={reset} className="agent-btn-ghost" title="대화 초기화">
              ↺
            </button>
          )}
          {floating && (
            <button onClick={() => setIsOpen(false)} className="agent-btn-ghost" title="닫기">
              ✕
            </button>
          )}
        </div>
      </div>

      {/* 메시지 영역 */}
      <div className="agent-chat-messages" ref={messagesRef}>
        {messages.length === 0 && status === "idle" && (
          <div className="agent-empty">
            <p className="agent-empty-title">안녕하세요! 👋</p>
            <p className="agent-empty-subtitle">
              {siteName}에 대해 무엇이든 물어보세요.
            </p>
            <div className="agent-prompts">
              {[
                "사무실에 적합한 복합기 추천해주세요",
                "컬러 복합기 렌탈 가격이 궁금해요",
                "토너 호환 제품 있나요?",
                "A3 출력 가능한 복합기 알려주세요",
              ].map((prompt) => (
                <button
                  key={prompt}
                  className="agent-prompt-chip"
                  onClick={() => { setInput(""); sendMessage(prompt); }}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`agent-msg agent-msg-${msg.role}`}>
            {msg.role === "user" ? (
              <div className="agent-msg-user-bubble">{msg.content}</div>
            ) : (
              <div className={`agent-msg-assistant ${msg.isError ? "agent-msg-error" : ""}`}>
                {/* Gemini-style 사고 과정 (접기/펼치기) */}
                {msg.thoughts && msg.thoughts.length > 0 && (
                  <ThinkingIndicator thoughts={msg.thoughts} isStreaming={false} />
                )}
                {/* 도구 사용 뱃지 */}
                {msg.tools && msg.tools.length > 0 && (
                  <div className="agent-msg-tools-used">
                    {msg.tools.map((t, j) => (
                      <span key={j} className="agent-msg-tool-badge">
                        🔧 {t.name}
                      </span>
                    ))}
                  </div>
                )}
                <SimpleMarkdown text={msg.content} />
              </div>
            )}
          </div>
        ))}

        {/* 스트리밍 중 상태 */}
        {status === "streaming" && (
          <div className="agent-msg agent-msg-assistant">
            <div className="agent-msg-assistant">
              <ThinkingIndicator thoughts={currentThoughts} isStreaming={true} />
              <ToolCallIndicator tools={currentTools} />
              <StreamingText text={streamingText} />
              {!streamingText && !currentThoughts.length && !currentTools.length && (
                <div className="agent-loading">
                  <span className="agent-loading-dot" />
                  <span className="agent-loading-dot" />
                  <span className="agent-loading-dot" />
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="agent-error-banner">{error}</div>
        )}

      </div>

      {/* 입력 영역 */}
      <form className="agent-chat-input" onSubmit={handleSubmit}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={status === "streaming"}
          className="agent-textarea"
        />
        {status === "streaming" ? (
          <button type="button" onClick={stop} className="agent-btn-stop" title="중지">
            ■
          </button>
        ) : (
          <button
            type="submit"
            disabled={!input.trim()}
            className="agent-btn-send"
            title="전송"
          >
            ↑
          </button>
        )}
      </form>
    </div>
  );
}
